import { Column, HeaderGroup, Table } from '@tanstack/react-table';

import cn from 'classnames';
import { Fragment, InputHTMLAttributes, useEffect, useState } from 'react';
import ColumnFilterSelect from '../inputs/ColumnFilterSelect';
import SafeInput from '../inputs/SafeInput';

interface TableColumnFiltersProps {
    headerGroups: HeaderGroup<any>[];
    tableInstance: Table<any>;
    thClassNames?: string;
}

const TableColumnFilters = ({
    headerGroups,
    tableInstance,
    thClassNames = 'bg-background-dark'
}: TableColumnFiltersProps) => {
    return (
        <Fragment>
            {headerGroups.map((headerGroup) => (
                <tr key={`${headerGroup.id}-filters`}>
                    {headerGroup.headers.map((header, index) => {
                        return (
                            <td
                                key={`${header.id}-filter-${index}`}
                                className={cn(
                                    'py-3 border-t border-brand-neutral-700 text-xs font-normal lg:text-sm text-brand-text sticky top-[35px] z-[1]',
                                    {
                                        ['pl-6 pr-4']: index === 0,
                                        ['pr-4']: index !== 0 && index !== headerGroup.headers.length - 1,
                                        [thClassNames]: thClassNames
                                    }
                                )}
                                style={{ width: header.column.getSize() }}>
                                <div>
                                    {header.column.getCanFilter() && (
                                        <Filter column={header.column} tableInstance={tableInstance} />
                                    )}
                                </div>
                            </td>
                        );
                    })}
                </tr>
            ))}
        </Fragment>
    );
};

export default TableColumnFilters;

function Filter({ column, tableInstance }: { column: Column<any, unknown>; tableInstance: Table<any> }) {
    const columnFilterValue = column.getFilterValue();
    const firstValue = tableInstance.getPreFilteredRowModel().flatRows[0]?.getValue(column.id);

    const hasColumnId = Object.keys(selectFilterOptions).includes(column.id);
    const optionId = hasColumnId ? column.id : 'default';

    return (
        <div className="text-brand-text font-normal">
            {typeof firstValue === 'boolean' || hasColumnId ? (
                <ColumnFilterSelect
                    column={column}
                    onChange={(option) => column.setFilterValue(option.value)}
                    selected={selectFilterOptions[optionId].find(
                        (option) => option.value === (columnFilterValue ?? '')
                    )}
                    options={selectFilterOptions[optionId]}
                />
            ) : (
                <DebouncedInput
                    type="text"
                    value={(columnFilterValue ?? '') as string}
                    onChange={(value) => column.setFilterValue(value)}
                    placeholder={`Search`}
                    className="w-36 border shadow"
                    list={column.id + 'list'}
                />
            )}
        </div>
    );
}

function DebouncedInput({
    value: initialValue,
    onChange,
    debounce = 500,
    ...props
}: {
    value: string | number;
    onChange: (value: string | number) => void;
    debounce?: number;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            onChange(value);
        }, debounce);

        return () => clearTimeout(timeout);
    }, [value]);

    return (
        <SafeInput
            {...props}
            value={value}
            className="!p-0 !px-2 !text-2xs !rounded-sm"
            onChange={(e) => setValue(e.target.value)}
        />
    );
}

const selectFilterOptions = {
    active: [
        { label: 'Show all', value: '' },
        { label: 'Active', value: true },
        { label: 'Inactive', value: false }
    ],
    // requestStatus: [
    //     { label: 'Show all', value: '' },
    //     { label: 'INITIATED', value: 'INITIATED' },
    //     { label: 'APPROVED', value: 'APPROVED' },
    //     { label: 'REJECTED', value: 'REJECTED' },
    //     { label: 'CANCELED', value: 'CANCELED' }
    // ],
    userStatus: [
        { label: 'Show all', value: '' },
        { label: 'Active', value: 'Active' },
        { label: 'Inactive', value: 'Inactive' },
        { label: 'Locked', value: 'Locked' }
    ],
    tableMyEntity: [
        { label: 'Show all', value: '' },
        { label: 'True', value: 'True' },
        { label: 'False', value: 'False' }
    ],
    tableTwoFactorEnabled: [
        { label: 'Show all', value: '' },
        { label: 'Active', value: 'Enabled' },
        { label: 'Inactive', value: 'Disabled' }
    ],
    status: [
        { label: 'Show all', value: '' },
        { label: 'Ok', value: 'Ok' },
        { label: 'Canceled', value: 'Canceled' }
    ],
    tradeStatus: [
        { label: 'Show all', value: '' },
        { label: 'Normal', value: 'Normal' },
        { label: 'Exercised', value: 'Exercised' },
        { label: 'Expired', value: 'Expired' },
        { label: 'KnockedIn', value: 'KnockedIn' },
        { label: 'KnockedOut', value: 'KnockedOut' },
        { label: 'Cancelled', value: 'Cancelled' }
    ],
    twoFactorMode: [
        { label: 'Show all', value: '' },
        { label: 'None', value: 'None' },
        { label: 'Verify', value: 'Verify' },
        { label: 'Setup', value: 'Setup' }
    ],
    supportingDetailProvided: [
        { label: 'Show all', value: '' },
        { label: 'Submitted', value: 'true' },
        { label: 'Pending', value: 'false' }
    ],
    miFIDscope: [
        { label: 'Show all', value: '' },
        { label: 'Yes', value: true },
        { label: 'No', value: false }
    ],
    type: [
        { label: 'Show all', value: '' },
        { label: 'Crypto CFD', value: 'crypto Cfd' },
        { label: 'FX Spot', value: 'fx spot' },
        { label: 'Index CFD', value: 'index Cfd' },
        { label: 'Commodity CFD', value: 'commodity Cfd' }
    ],
    default: [
        { label: 'Show all', value: '' },
        { label: 'Enabled', value: true },
        { label: 'Disabled', value: false }
    ]
};
